import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, api } from './api';
import { t } from './i18n';

export type HeatmapKind = 'bus' | 'user';
export type DayPart = 'all' | 'morning' | 'midday' | 'afternoon' | 'evening' | 'night';
/** [lon, lat, positions, average speed km/h (buses)] */
export type HeatCell = [number, number, number, number];

export function useHeatmap(kind: HeatmapKind | null, days: number, part: DayPart) {
  return useQuery({
    queryKey: ['stats', 'heatmap', kind, days, part],
    queryFn: () => api<{ cells: HeatCell[] }>('/stats/heatmap', { query: { kind: kind!, days, part } }),
    enabled: !!kind,
    staleTime: 5 * 60_000,
  });
}

interface Overview {
  hour: number;
  updated_until: string | null;
  km: { today: number; yesterday_same_time: number };
  requests: {
    today: number;
    yesterday_same_time: number;
    by_hour: { today: number[]; yesterday: number[] };
  };
  buses_by_hour: { today: number[]; yesterday: number[] };
  top_stations: Array<{ _id: string; name: string; n: number }>;
}

export function useOverview() {
  return useQuery({
    queryKey: ['stats', 'overview'],
    queryFn: () => api<Overview>('/stats/overview'),
    refetchInterval: 2 * 60_000,
  });
}

export type LineCheckStatus = 'ok' | 'wrong_zone' | 'off_route' | 'unassigned' | 'unknown';
export interface ZoneRef {
  _id: string;
  name: string;
  bus_sign: string;
  color: string;
}
export interface LineCheck {
  status: LineCheckStatus;
  zone_share: number | null;
  suggested_zone: ZoneRef | null;
  suggested_share: number | null;
  samples: number;
  since: string;
  at: string;
}
export interface FlaggedBus {
  _id: string;
  name?: string;
  arvento_id?: string;
  number?: string;
  is_visible?: boolean;
  zone: ZoneRef | null;
  line_check: LineCheck;
}

/** Buses that do not seem to drive their zone (refreshed every 30 s). */
export function useLineChecks() {
  return useQuery({
    queryKey: ['buses', 'line-checks'],
    queryFn: () => api<{ items: FlaggedBus[] }>('/buses/line-checks'),
    refetchInterval: 30_000,
  });
}

/** Moves a bus to the zone the line check suggests. */
export function useMoveToZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bus, zone }: { bus: string; zone: string }) =>
      api(`/buses/${bus}/zone`, { method: 'PUT', body: { zone } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['buses'] });
      toast.success(t('Line updated'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not assign')),
  });
}

interface Demand {
  days: number;
  total: number;
  previous_total: number;
  by_hour: number[];
  /** Monday first. */
  by_weekday: number[];
  by_day: Array<{ d: string; n: number }>;
  stations: Array<{ _id: string; name: string; n: number; prev: number }>;
  lines: Array<{
    _id: string | null;
    name: string | null;
    sign: string;
    color: string;
    n: number;
    prev: number;
  }>;
}

/** Passenger requests over the last `days` days against the days before. */
export function useDemand(days: number) {
  return useQuery({
    queryKey: ['stats', 'demand', days],
    queryFn: () => api<Demand>('/stats/demand', { query: { days } }),
    staleTime: 5 * 60_000,
  });
}

interface StationDemand {
  station?: { _id: string; name: string; lng: number; lat: number };
  total: number;
  /** Half of the requests came from within this distance of the station. */
  median_m: number | null;
  cells: HeatCell[];
}

/** Where the people asking about one station were. */
export function useStationDemand(id: string | null, days: number) {
  return useQuery({
    queryKey: ['stats', 'demand', 'station', id, days],
    queryFn: () => api<StationDemand>(`/stats/demand/stations/${id}`, { query: { days } }),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}
